import { useState, useEffect } from 'react';

interface Props {
  defaults?: { eventName?: string; eventType?: string; eventDate?: string };
  onBack: () => void;
  onNext: (values: { eventName: string; eventType?: string; eventDate: string }) => void;
}

const EVENT_TYPES = [
  'Neighborhood Bar',
  'Standard Cocktail Lounge',
  'Festival / Ticketed Event',
  'Stadium / Arena Pricing',
  'Ultra-Premium / VIP Event'
] as const;

const normalizeDateInput = (value?: string) => {
  if (!value) return '';
  const isoMatch = /^\d{4}-\d{2}-\d{2}$/;
  if (isoMatch.test(value)) return value;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toISOString().split('T')[0];
};

const MetadataBasics = ({ defaults, onBack, onNext }: Props) => {
  const [eventName, setEventName] = useState(defaults?.eventName ?? '');
  const [eventType, setEventType] = useState<string>(EVENT_TYPES.includes((defaults?.eventType as any) ?? '') ? (defaults?.eventType as any) : '');
  const [eventDate, setEventDate] = useState<string>(normalizeDateInput(defaults?.eventDate));
  const [valid, setValid] = useState(false);

  useEffect(() => {
    // Required: event name and event date; event type is optional
    setValid(eventName.trim().length > 0 && eventDate.trim().length > 0);
  }, [eventName, eventDate]);

  return (
    <div className="theme-card p-8 bg-white">
      {/* Heading handled at page level */}
      <div className="mt-8 space-y-5">
        {/* Event name row: 12-col grid, label 4, input 6 */}
        <div className="grid gap-3 md:grid-cols-12 md:items-center">
          <div className="text-left md:col-span-4 flex items-center">
            <span className="text-sm font-medium text-slate-700">Event name <span className="text-red-600">*</span></span>
          </div>
          <div className="md:col-span-6">
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
              placeholder="e.g., Summer Kickoff Party"
            />
            {/* no helper for event name */}
          </div>
        </div>

        {/* Event date row */}
        <div className="grid gap-3 md:grid-cols-12 md:items-center">
          <div className="text-left md:col-span-4 flex items-center">
            <span className="text-sm font-medium text-slate-700">Event date <span className="text-red-600">*</span></span>
          </div>
          <div className="md:col-span-6">
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
            />
            <p className="mt-1 text-xs text-slate-500">We’ll use the date to analyze similar events in your area, around your date.</p>
          </div>
        </div>

        {/* Event type row */}
        <div className="grid gap-3 md:grid-cols-12 md:items-center">
          <div className="text-left md:col-span-4 flex items-center">
            <span className="text-sm font-medium text-slate-700">Event type</span>
          </div>
          <div className="md:col-span-6">
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
            >
              <option value="" disabled>Select an event type</option>
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">Here's what we think the type of your event is, feel free to select what you think suits your event the best</p>
          </div>
        </div>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <button type="button" className="btn-secondary" onClick={onBack}>Back</button>
        <button type="button" className="btn-primary" disabled={!valid} onClick={() => onNext({ eventName: eventName.trim(), eventType: eventType || undefined, eventDate })}>Next</button>
      </div>
    </div>
  );
};

export default MetadataBasics;
